import { useCallback } from 'react';
import { customerService } from '../services/customerService';
import api from '../services/api';
import toast from 'react-hot-toast';
import { isZeroPricedCartItem, itemLabel } from '../contexts/CartContext';

/**
 * Hook do ladowania transakcji z zamowienia
 * Pozwala zaladowac istniejaca transakcje (np. z zamowienia) do koszyka POS
 */
const useOrderTransaction = ({
  setCart,
  setCurrentTransaction,
  setAppliedDiscounts,
  setCartDiscountTotal,
  setSelectedCustomer,
  setActiveTab,
  setLoading,
  setError
}) => {
  /**
   * Zaladowanie transakcji z zamowienia do koszyka POS
   */
  const loadTransactionFromOrder = useCallback(async (transactionId) => {
    try {
      setLoading(true);

      const response = await api.get(`/pos/cart/${transactionId}`);

      const data = response.data;

      if (data.success) {
        const { transakcja, pozycje, rabaty } = data.data;

        // Ustaw aktualna transakcje
        setCurrentTransaction({
          id: transakcja.id,
          numer_transakcji: transakcja.numer_transakcji,
          status: transakcja.status
        });

        // Przeksztalc pozycje na format koszyka
        // WAZNE: Zaokraglij ceny do 2 miejsc dziesietnych - spojnosc z drukarka fiskalna
        const cartItems = pozycje.map(item => {
          const price = Math.round(parseFloat(item.cena_jednostkowa) * 100) / 100;
          return {
            id: item.id,
            produkt_id: item.produkt_id,
            name: item.nazwa_produktu,
            nazwa: item.nazwa_produktu,
            cena: price,
            price: price,
            quantity: parseInt(item.ilosc),
            vat: parseFloat(item.stawka_vat),
            // stawka_vat MUSI wejsc do koszyka pod ta sama nazwa co przy
            // sprzedazy z katalogu - silnik rabatu i druk fiskalny czytaja
            // item.stawka_vat (bez tego pozycja 0% VAT drukowala sie jako 23%).
            stawka_vat: parseFloat(item.stawka_vat),
            typ_produktu: item.typ_produktu,
            wykluczony_z_rabatow: item.wykluczony_z_rabatow,
            wartosc_netto: Math.round(parseFloat(item.wartosc_netto) * 100) / 100,
            wartosc_brutto: Math.round(parseFloat(item.wartosc_brutto) * 100) / 100,
            available_stock: item.available_stock,
            stock_warning: item.stock_warning,
            lp: item.lp
          };
        });

        // Pozycje bez ceny (dokladnie 0.00) nie wchodza do koszyka - decyzja
        // biznesowa wlasciciela (2026-08): towar z cena 0.00 nie wchodzi do
        // koszyka, minimum 0.01 zl. Protokol XML (pxml.txt) NIE stawia warunku
        // > 0 - to nie wymog drukarki. Kaucje (takze 0.00) sa z blokady wylaczone.
        const zeroPriced = cartItems.filter(isZeroPricedCartItem);
        const sellableItems = cartItems.filter(item => !isZeroPricedCartItem(item));

        if (zeroPriced.length > 0) {
          toast.error(
            `Pominieto pozycje bez ceny (0,00 zl): ${zeroPriced.map(itemLabel).join(', ')}. ` +
            'Uzupelnij cennik i dodaj je ponownie.'
          );
        }

        setCart(sellableItems);

        // Ustaw rabaty jesli sa.
        // NORMALIZACJA POL: GET /pos/cart zwraca wiersze rabaty_uzycie
        // (id = uzycie_id) z dolaczonymi polami rabatu (rabat_nazwa,
        // typ_rabatu, rabat_wartosc). Silnik rabatu w CartContext czyta
        // discount.wartosc / discount.nazwa / discount.uzycie_id - bez
        // przepisania tych pol rabat procentowy z zamowienia liczyl sie
        // jako 0 (wartosc undefined) i znikal z wyswietlacza i platnosci.
        if (rabaty && rabaty.length > 0) {
          const znormalizowane = rabaty.map(r => ({
            ...r,
            id: r.rabat_id ?? r.id,
            uzycie_id: r.uzycie_id ?? r.id,
            nazwa: r.nazwa ?? r.rabat_nazwa,
            typ_rabatu: r.typ_rabatu,
            wartosc: r.wartosc ?? r.rabat_wartosc,
            kwota_rabatu: parseFloat(r.kwota_rabatu || 0),
            czasowe_id: r.czasowe_id ?? `uzycie_${r.uzycie_id ?? r.id}`,
          }));
          setAppliedDiscounts(znormalizowane);
          const totalDiscount = znormalizowane.reduce((sum, r) => sum + (r.kwota_rabatu || 0), 0);
          setCartDiscountTotal(totalDiscount);
        }

        // Pobierz dane klienta jesli jest przypisany
        if (transakcja.klient_id) {
          try {
            const customerResponse = await customerService.getById(transakcja.klient_id);
            if (customerResponse.success && customerResponse.data) {
              setSelectedCustomer(customerResponse.data);
            }
          } catch (err) {
            console.error('Nie udalo sie zaladowac klienta:', err);
          }
        }

        // Ustaw aktywna zakladke na POS
        setActiveTab('pos');
      } else {
        console.error('Blad ladowania transakcji:', data.message);
        setError(data.message || 'Blad ladowania transakcji');
      }
    } catch (err) {
      console.error('Blad podczas ladowania transakcji z zamowienia:', err);
      setError('Blad polaczenia z serwerem');
    } finally {
      setLoading(false);
    }
  }, [setCart, setCurrentTransaction, setAppliedDiscounts, setCartDiscountTotal,
      setSelectedCustomer, setActiveTab, setLoading, setError]);

  return {
    loadTransactionFromOrder
  };
};

export default useOrderTransaction;
